/**
 * «Об'єкти»: a card per object — name, model, SN, region, connection,
 * charge, history download, internal arbitrage; D4 (prototype v14): the
 * access of Phage.Energy — «Очікує доступу Phage.Energy», «Доступ
 * Phage.Energy закрито» (arbitrage stopped), «Немає права керування». No
 * objects — «Ще немає об'єктів» and «Додати об'єкт».
 */
import { Link } from 'react-router-dom';
import { type SiteResponse, todayInTz } from 'shared';

import { AppShell } from '../../components/AppShell.js';
import { fmtNum, fmtSince, modelLabel, serialOf } from '../../components/format.js';
import { regionLabel } from '../../components/regions.js';
import { connOf, connState, histState } from '../../components/site-state.js';
import { useSiteAnalysis, useSiteOverview, useSitePotential } from '../../hooks/use-site-results.js';
import { useSites } from '../../hooks/use-sites.js';
import { useLocale } from '../../i18n/locale-context.js';
import { HistChip } from './SiteHeader.js';

function SiteCard({ site }: { site: SiteResponse }) {
  const { t, tf, locale } = useLocale();
  const overview = useSiteOverview(site.id);
  const analysis = useSiteAnalysis(site.id);
  const latest = overview.data?.latest;
  const conn = connState(connOf(site), latest);
  // D4: the access is shown whatever the connection (no right to manage with an offline inverter too).
  const access = site.phage_access.status;
  const potential = useSitePotential(site.id, false);
  // D4: waiting for Phage.Energy's access — the history download has not started.
  const hist =
    site.phage_access.status === 'pending' ? null : histState(analysis.data, potential.data?.status === 'ok');
  const today = todayInTz(site.timezone);
  const meta = [
    modelLabel(site.model),
    `SN ${serialOf(site.vendor_site_id)}`,
    regionLabel(site.region, locale),
  ]
    .filter(Boolean)
    .join(' · ');
  const arb =
    conn === 'closed'
      ? t('site.arb.stopped')
      : t(site.internal_arbitrage_enabled ? 'site.arb.on_short' : 'site.arb.off_short');

  return (
    <Link className="obj-card" to={`/app/sites/${site.id}`}>
      <div>
        <h2>{site.name}</h2>
        <div className="meta num">{meta}</div>
        <div className="chips">
          {access !== 'open' ? <span className="chip warn">{t(`site.card.${access}`)}</span> : null}
          {conn === 'ok' || conn === 'noedit' ? (
            <span className="chip">
              <span className="dot" aria-hidden="true" />
              {t('site.conn.ok')}
            </span>
          ) : null}
          {conn === 'off' && latest ? (
            <span className="chip warn">
              {tf('site.conn.off', { time: fmtSince(latest.ts, site.timezone, today) })}
            </span>
          ) : null}
          {conn === 'down' && site.vendor_down_since ? (
            <span className="chip">
              {tf('site.conn.down', { time: fmtSince(site.vendor_down_since, site.timezone, today) })}
            </span>
          ) : null}
          {conn === 'never' ? <span className="chip">{t('site.conn.never')}</span> : null}
          {hist && hist.kind !== 'done' ? <HistChip hist={hist} /> : null}
          {conn === 'pending' ? null : (
            <span className="chip">
              <span className="k">{t('site.chip.arb')}</span>
              {arb}
            </span>
          )}
        </div>
      </div>
      <div className="soc">
        <span className="sub">{t('site.card.soc')}</span>
        {latest?.soc_pct != null ? (
          <b className="num">{fmtNum(locale, latest.soc_pct)} %</b>
        ) : (
          <b className="small">{t('site.card.no_data')}</b>
        )}
      </div>
    </Link>
  );
}

export function SitesPage() {
  const { t } = useLocale();
  const sites = useSites();
  return (
    <AppShell>
      <div className="view">
        <div className="page-head">
          <h1>{t('sites.title')}</h1>
          <Link className="btn primary" to="/app/sites/new">
            {t('sites.add')}
          </Link>
        </div>
        {sites.isLoading ? <p className="loading">{t('common.loading')}</p> : null}
        {sites.isError ? <p className="hist-note err">{t('common.load_error')}</p> : null}
        {sites.data && sites.data.length === 0 ? (
          <section className="card empty-card">
            <h2>{t('sites.empty.title')}</h2>
            <p className="sub">{t('sites.empty.sub')}</p>
            <Link className="btn primary" to="/app/sites/new">
              {t('sites.add')}
            </Link>
          </section>
        ) : null}
        {sites.data && sites.data.length > 0 ? (
          <div className="obj-list">
            {sites.data.map((s) => (
              <SiteCard key={s.id} site={s} />
            ))}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
